import { Pagination } from '@/types';
import apiClient from '../apiClient';

export interface InvoiceItem {
  id?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount?: number;
  type?: 'consultation' | 'procedure' | 'medication' | 'lab_test' | 'imaging' | 'other';
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  patientId: string;
  /** The site the bill was raised at; null on a bill older than the column. */
  branchId?: string | null;
  patient?: {
    id: string;
    firstName: string;
    lastName: string;
    mrn: string;
  };
  issuedById?: string;
  issuedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  date: string;
  dueDate?: string;
  status: 'draft' | 'pending' | 'paid' | 'partially_paid' | 'overdue' | 'cancelled';
  subtotal: number;
  taxAmount: number;
  taxRate: number;
  discountAmount: number;
  total: number;
  amountPaid: number;
  balance: number;
  paymentMethod?: 'cash' | 'card' | 'insurance' | 'bank_transfer' | 'mobile_payment';
  notes?: string;
  appointmentId?: string;
  items: InvoiceItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateInvoiceDto {
  patientId: string;
  date: string;
  dueDate?: string;
  taxRate?: number;
  discountAmount?: number;
  paymentMethod?: string;
  notes?: string;
  appointmentId?: string;
  items: InvoiceItem[];
  amount?: number;
}

export interface UpdateInvoiceDto {
  patientId?: string;
  date?: string;
  dueDate?: string;
  status?: string;
  taxRate?: number;
  discountAmount?: number;
  paymentMethod?: string;
  notes?: string;
  appointmentId?: string;
  items?: InvoiceItem[];
  amountPaid?: number;
  amount?: number;
}

export interface FilterInvoiceDto {
  page?: number;
  limit?: number;
  patientId?: string;
  status?: string;
  paymentMethod?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface ProcessPaymentDto {
  amount: number;
  paymentMethod: string;
}

export interface InvoiceStatistics {
  totalRevenue: number;
  totalOutstanding: number;
  totalPaid: number;
  totalOverdue: number;
  invoicesByStatus: Record<string, number>;
}

// ── Insurance claims (server-derived from insurance invoices) ──────────────
export type ClaimStatus =
  | 'draft'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'partially_approved'
  | 'denied'
  | 'appealed';

export interface Claim {
  invoiceId: string;
  invoiceNumber: string;
  patientName: string;
  patientId: string;
  date: string;
  total: number;
  balance: number;
  payer: string | null;
  payerLabel: string;
  authorizationNumber: string | null;
  status: ClaimStatus;
}

export interface ClaimsSummary {
  /** Total number of insurance claims (unfiltered). */
  count: number;
  dollarsOut: number;
  dollarsIn: number;
  submitted: number;
  approved: number;
  denied: number;
  draft: number;
}

export interface FilterClaimsDto {
  page?: number;
  limit?: number;
  search?: string;
  /** 'all' or a ClaimStatus. */
  status?: string;
  /** Payer value (e.g. 'allianz'), 'unspecified', or 'all'. */
  payer?: string;
}

export interface ClaimsResponse {
  data: Claim[];
  pagination: Pagination;
  /** KPI totals across ALL insurance claims (not just the current page). */
  summary: ClaimsSummary;
}

export const billingApi = {
  // Get all invoices with filters
  getInvoices: async (filters: FilterInvoiceDto = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.patientId) params.append('patientId', filters.patientId);
    if (filters.status) params.append('status', filters.status);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);

    const response = await apiClient.get(`/billing?${params.toString()}`);
    return response.data as {
      data: Invoice[];
      pagination: Pagination
    };
  },

  // Get insurance claims — server filters/summarises insurance invoices
  // (payer/auth live in encrypted notes, so this can't be done client-side).
  getClaims: async (filters: FilterClaimsDto = {}): Promise<ClaimsResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.payer) params.append('payer', filters.payer);

    const response = await apiClient.get(`/billing/claims?${params.toString()}`);
    return response.data as ClaimsResponse;
  },

  // Get single invoice by ID
  getInvoiceById: async (id: string) => {
    const response = await apiClient.get(`/billing/${id}`);
    return response.data;
  },

  // Create new invoice
  createInvoice: async (data: CreateInvoiceDto) => {
    const response = await apiClient.post('/billing', data);
    return response.data;
  },

  // Update invoice
  updateInvoice: async (id: string, data: UpdateInvoiceDto) => {
    const response = await apiClient.patch(`/billing/${id}`, data);
    return response.data;
  },

  // Process payment for invoice
  processPayment: async (id: string, data: ProcessPaymentDto) => {
    const response = await apiClient.post(`/billing/${id}/payment`, data);
    return response.data;
  },

  // Delete invoice
  deleteInvoice: async (id: string) => {
    const response = await apiClient.delete(`/billing/${id}`);
    return response.data;
  },

  // Get billing statistics
  getStatistics: async () => {
    const response = await apiClient.get('/billing/statistics');
    return response.data;
  },
};
